import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicLines,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsMakeGeometry,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';

const SPHERE_SEGMENTS: number = 4;

/**
 * Line segments of three circles of radius 2 in the XZ, XY and YZ planes, as `gl.LINES` vertex pairs. The
 * legacy line sphere primitive placed them off center the same way.
 */
function makeLineSphereData(segmentsCount: number): HsRegularGeometryData {
  const vertices: number[] = [];
  const circles: ((cos: number, sin: number) => [number, number, number])[] = [
    (cos: number, sin: number) => [cos + 1, 0, sin],
    (cos: number, sin: number) => [cos + 1, sin, 0],
    (cos: number, sin: number) => [1, cos, sin],
  ];
  for (const circle of circles) {
    for (let i: number = 0; i <= segmentsCount; i++) {
      const angle: number = (i / segmentsCount) * Math.PI * 2;
      const point: [number, number, number] = circle(Math.cos(angle) * 2, Math.sin(angle) * 2);
      vertices.push(...point);
      // Inner points end one segment and start the next.
      if (i > 0 && i < segmentsCount) {
        vertices.push(...point);
      }
    }
  }
  return new HsRegularGeometryData(vertices, []);
}

/** A line object: a coarse wireframe sphere drawn with line segments. */
@Component({
  selector: 'hyperscene-lines',
  templateUrl: './lines.component.html',
  styleUrls: ['./lines.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class LinesDemo extends DemoViewerPage {
  protected initScene(viewer: HsHypersceneViewer): void {
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      makeLineSphereData(SPHERE_SEGMENTS)
    );
    if (geometry === null) {
      return;
    }
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth();
    const material: HsMaterial = new HsDefaultMaterial(viewer.gl, shaderData.makeShader());
    material.color.set(1, 0, 0);
    const line: HsGraphicLines = new HsGraphicLines(getUuid(), 'Line', geometry, material);
    line.worldMatrix.compose([0, 2, 0], [0, 0, 0, 1], [2, 2, 2]);
    line.updateBoundingSphere();
    viewer.add(line);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition([2, 10, 30]);
    viewer.camera.updateProjectionMatrix();
  }
}
